import { apiClient } from './apiService';
import {
  FOOD_DELIVERY_INTEGRATIONS,
  FOOD_DELIVERY_INTEGRATION_DETAIL,
  FOOD_DELIVERY_INTEGRATION_VALIDATE,
  FOOD_DELIVERY_INTEGRATION_HEALTH,
  FOOD_DELIVERY_PROVIDERS,
  FOOD_DELIVERY_INTEGRATION_REMOTE_STORES,
  FOOD_DELIVERY_INTEGRATION_BACKFILL,
  FOOD_DELIVERY_ORDERS,
  FOOD_DELIVERY_ORDER_DETAIL,
  FOOD_DELIVERY_ORDER_REPROCESS,
  FOOD_DELIVERY_ORDER_ACCEPT,
  FOOD_DELIVERY_ORDER_REJECT,
  FOOD_DELIVERY_ORDER_READY,
  FOOD_DELIVERY_ORDER_DISPATCHED,
  FOOD_DELIVERY_ORDER_DELIVERED,
  FOOD_DELIVERY_ORDER_CANCEL_ACK,
  FOOD_DELIVERY_WEBHOOK_LOGS,
  FOOD_DELIVERY_WEBHOOK_LOG_RETRY,
  FOOD_DELIVERY_MENU_IMPORT,
  FOOD_DELIVERY_MAPPINGS,
  FOOD_DELIVERY_MAPPING_DETAIL,
  FOOD_DELIVERY_MODIFIER_MAPPINGS,
  FOOD_DELIVERY_MODIFIER_MAPPING_DETAIL,
  FOOD_DELIVERY_SYNC,
  FOOD_DELIVERY_SYNC_LOGS,
  FOOD_DELIVERY_OPERATIONS,
  FOOD_DELIVERY_OPERATIONS_STATUS,
  FOOD_DELIVERY_OPERATIONS_WORKING_HOURS,
  FOOD_DELIVERY_OPERATIONS_DELIVERY_DURATION,
  FOOD_DELIVERY_HEALTH,
  FOOD_DELIVERY_METRICS,
  FOOD_DELIVERY_ALERTS,
  FOOD_DELIVERY_COMPANY_INTEGRATIONS,
  FOOD_DELIVERY_INTEGRATIONS_BULK,
  FOOD_DELIVERY_COMPANY_HEALTH,
  FOOD_DELIVERY_COMPANY_METRICS,
  FOOD_DELIVERY_CATEGORIES,
  FOOD_DELIVERY_CATEGORY_AVAILABILITY,
  FOOD_DELIVERY_CLAIMS,
  FOOD_DELIVERY_CLAIM_REASONS,
  FOOD_DELIVERY_CLAIM_REFRESH,
  FOOD_DELIVERY_CLAIM_ACCEPT,
  FOOD_DELIVERY_CLAIM_UNRESOLVE,
  FOOD_DELIVERY_ORDER_INVOICE,
  FOOD_DELIVERY_REVIEWS,
  FOOD_DELIVERY_REVIEW_RATING,
  FOOD_DELIVERY_REVIEW_REPLY,
  FOOD_DELIVERY_COMPANY_ALERTS
} from './endpoints';
import {
  BackfillResult,
  BranchHealthOverview,
  BranchMetrics,
  FoodProviderInfo,
  RemoteStore,
  BranchMonitoring,
  BulkActionResult,
  BulkIntegrationAction,
  CompanyHealthOverview,
  CompanyMetrics,
  ClaimReasonTables,
  CompanyMonitoring,
  ExternalClaim,
  ExternalClaimStatus,
  ExternalMenuCategory,
  ProviderRatingSummary,
  ProviderReview,
  CreateFoodIntegrationPayload,
  ExternalModifierMapping,
  ExternalOrderDetail,
  ExternalOrderRow,
  ExternalOrderStatus,
  ExternalProductMapping,
  ExternalSyncLog,
  ExternalSyncType,
  ExternalWebhookLog,
  ExternalWebhookStatus,
  FoodIntegration,
  HealthResult,
  MenuImportResult,
  OperationResult,
  Paginated,
  RestaurantOpsView,
  RestaurantStatus,
  SyncRunResult,
  UpdateFoodIntegrationPayload,
  UpsertModifierMappingPayload,
  UpsertProductMappingPayload,
  ValidateResult,
  WorkingHoursInterval
} from '@/types/FoodDelivery.interface';

const byId = (template: string, id: number) =>
  template.replace(':id', String(id));

const byMappingId = (template: string, id: number, mappingId: number) =>
  template.replace(':id', String(id)).replace(':mappingId', String(mappingId));

export const foodDeliveryService = {
  async listByBranch(branchId: number): Promise<FoodIntegration[]> {
    const response = await apiClient.get<FoodIntegration[]>(
      FOOD_DELIVERY_INTEGRATIONS,
      { branchId }
    );
    return response.data;
  },

  async create(
    payload: CreateFoodIntegrationPayload
  ): Promise<FoodIntegration> {
    const response = await apiClient.post<FoodIntegration>(
      FOOD_DELIVERY_INTEGRATIONS,
      payload
    );
    return response.data;
  },

  async update(
    id: number,
    payload: UpdateFoodIntegrationPayload
  ): Promise<FoodIntegration> {
    const response = await apiClient.put<FoodIntegration>(
      byId(FOOD_DELIVERY_INTEGRATION_DETAIL, id),
      payload
    );
    return response.data;
  },

  async remove(id: number): Promise<void> {
    await apiClient.delete<void>(byId(FOOD_DELIVERY_INTEGRATION_DETAIL, id));
  },

  async validate(id: number): Promise<ValidateResult> {
    const response = await apiClient.post<ValidateResult>(
      byId(FOOD_DELIVERY_INTEGRATION_VALIDATE, id)
    );
    return response.data;
  },

  /** Kurulum formunun alan şeması — backend adapter'larından üretilir. */
  async listProviders(): Promise<FoodProviderInfo[]> {
    const response = await apiClient.get<FoodProviderInfo[]>(
      FOOD_DELIVERY_PROVIDERS
    );
    return response.data;
  },

  /** Sağlayıcıdaki mağazalar (kurulumda mağaza kimliğini seçmek için). */
  async listRemoteStores(id: number): Promise<RemoteStore[]> {
    const response = await apiClient.get<RemoteStore[]>(
      byId(FOOD_DELIVERY_INTEGRATION_REMOTE_STORES, id)
    );
    return response.data;
  },

  async health(id: number): Promise<HealthResult> {
    const response = await apiClient.get<HealthResult>(
      byId(FOOD_DELIVERY_INTEGRATION_HEALTH, id)
    );
    return response.data;
  },

  /**
   * Kesinti sonrası geçmiş siparişleri platformdan geri getirir. Normal
   * yoklama yalnız son 30 dakikaya bakar; uzun kesintide aradaki siparişler
   * bu aksiyonla çekilir (en fazla 24 saat).
   */
  async backfillOrders(id: number, hours: number): Promise<BackfillResult> {
    const response = await apiClient.post<BackfillResult>(
      `${byId(FOOD_DELIVERY_INTEGRATION_BACKFILL, id)}?hours=${hours}`
    );
    return response.data;
  },

  // ---- Faz 3: external sipariş izleme + operasyon ----

  async listOrders(params: {
    branchId: number;
    status?: ExternalOrderStatus;
    page?: number;
    pageSize?: number;
  }): Promise<Paginated<ExternalOrderRow>> {
    const response = await apiClient.get<Paginated<ExternalOrderRow>>(
      FOOD_DELIVERY_ORDERS,
      params
    );
    return response.data;
  },

  async getOrder(id: number): Promise<ExternalOrderDetail> {
    const response = await apiClient.get<ExternalOrderDetail>(
      byId(FOOD_DELIVERY_ORDER_DETAIL, id)
    );
    return response.data;
  },

  async reprocessOrder(id: number): Promise<{ requeued: boolean }> {
    const response = await apiClient.post<{ requeued: boolean }>(
      byId(FOOD_DELIVERY_ORDER_REPROCESS, id)
    );
    return response.data;
  },

  // ---- Faz 4: sipariş operasyonları (accept/reject/ready) ----

  async acceptOrder(id: number): Promise<{
    accepted: boolean;
    outcome: string;
    internalOrderId?: number;
  }> {
    const response = await apiClient.post<{
      accepted: boolean;
      outcome: string;
      internalOrderId?: number;
    }>(byId(FOOD_DELIVERY_ORDER_ACCEPT, id));
    return response.data;
  },

  async rejectOrder(
    id: number,
    reasonCode: string
  ): Promise<{ rejected: boolean }> {
    const response = await apiClient.post<{ rejected: boolean }>(
      byId(FOOD_DELIVERY_ORDER_REJECT, id),
      {
        reasonCode
      }
    );
    return response.data;
  },

  async markOrderReady(id: number): Promise<{ ready: boolean }> {
    const response = await apiClient.post<{ ready: boolean }>(
      byId(FOOD_DELIVERY_ORDER_READY, id)
    );
    return response.data;
  },

  // ---- Faz 5: platform iptalini onayla (cancel-ack) ----

  /** "Yola çıktı" — yalnız kendi kuryesiyle çalışan (Model 1) mağazalarda. */
  async markOrderDispatched(id: number): Promise<{ dispatched: boolean }> {
    const response = await apiClient.post<{ dispatched: boolean }>(
      byId(FOOD_DELIVERY_ORDER_DISPATCHED, id)
    );
    return response.data;
  },

  /** "Teslim edildi" — yalnız kendi kuryesiyle çalışan (Model 1) mağazalarda. */
  async markOrderDelivered(id: number): Promise<{ delivered: boolean }> {
    const response = await apiClient.post<{ delivered: boolean }>(
      byId(FOOD_DELIVERY_ORDER_DELIVERED, id)
    );
    return response.data;
  },

  async cancelAckOrder(id: number): Promise<{ acknowledged: boolean }> {
    const response = await apiClient.post<{ acknowledged: boolean }>(
      byId(FOOD_DELIVERY_ORDER_CANCEL_ACK, id)
    );
    return response.data;
  },

  async listWebhookLogs(params: {
    branchId: number;
    status?: ExternalWebhookStatus;
  }): Promise<ExternalWebhookLog[]> {
    const response = await apiClient.get<ExternalWebhookLog[]>(
      FOOD_DELIVERY_WEBHOOK_LOGS,
      params
    );
    return response.data;
  },

  async retryWebhookLog(id: number): Promise<{ requeued: boolean }> {
    const response = await apiClient.post<{ requeued: boolean }>(
      byId(FOOD_DELIVERY_WEBHOOK_LOG_RETRY, id)
    );
    return response.data;
  },

  // ---- Faz 6: menü içe aktarma + ürün/modifier eşleştirme ----

  async importMenu(integrationId: number): Promise<MenuImportResult> {
    const response = await apiClient.post<MenuImportResult>(
      byId(FOOD_DELIVERY_MENU_IMPORT, integrationId)
    );
    return response.data;
  },

  async listMappings(integrationId: number): Promise<ExternalProductMapping[]> {
    const response = await apiClient.get<ExternalProductMapping[]>(
      byId(FOOD_DELIVERY_MAPPINGS, integrationId)
    );
    return response.data;
  },

  async upsertMapping(
    integrationId: number,
    payload: UpsertProductMappingPayload
  ): Promise<ExternalProductMapping> {
    const response = await apiClient.post<ExternalProductMapping>(
      byId(FOOD_DELIVERY_MAPPINGS, integrationId),
      payload
    );
    return response.data;
  },

  async deleteMapping(integrationId: number, mappingId: number): Promise<void> {
    await apiClient.delete<void>(
      byMappingId(FOOD_DELIVERY_MAPPING_DETAIL, integrationId, mappingId)
    );
  },

  async listModifierMappings(
    integrationId: number
  ): Promise<ExternalModifierMapping[]> {
    const response = await apiClient.get<ExternalModifierMapping[]>(
      byId(FOOD_DELIVERY_MODIFIER_MAPPINGS, integrationId)
    );
    return response.data;
  },

  async upsertModifierMapping(
    integrationId: number,
    payload: UpsertModifierMappingPayload
  ): Promise<ExternalModifierMapping> {
    const response = await apiClient.post<ExternalModifierMapping>(
      byId(FOOD_DELIVERY_MODIFIER_MAPPINGS, integrationId),
      payload
    );
    return response.data;
  },

  async deleteModifierMapping(
    integrationId: number,
    mappingId: number
  ): Promise<void> {
    await apiClient.delete<void>(
      byMappingId(
        FOOD_DELIVERY_MODIFIER_MAPPING_DETAIL,
        integrationId,
        mappingId
      )
    );
  },

  // ---- Faz 7: fiyat/availability senkronu ----

  async runSync(
    integrationId: number,
    types?: ExternalSyncType[]
  ): Promise<SyncRunResult> {
    const response = await apiClient.post<SyncRunResult>(
      byId(FOOD_DELIVERY_SYNC, integrationId),
      types && types.length ? { types } : {}
    );
    return response.data;
  },

  async listSyncLogs(
    integrationId: number,
    params?: { type?: ExternalSyncType; limit?: number }
  ): Promise<ExternalSyncLog[]> {
    const response = await apiClient.get<ExternalSyncLog[]>(
      byId(FOOD_DELIVERY_SYNC_LOGS, integrationId),
      params ?? {}
    );
    return response.data;
  },

  // ---- Faz 8: restoran operasyonları (açık/kapalı/yoğun, saat, süre) ----

  async getOperations(integrationId: number): Promise<RestaurantOpsView> {
    const response = await apiClient.get<RestaurantOpsView>(
      byId(FOOD_DELIVERY_OPERATIONS, integrationId)
    );
    return response.data;
  },

  async setRestaurantStatus(
    integrationId: number,
    status: RestaurantStatus
  ): Promise<OperationResult> {
    const response = await apiClient.post<OperationResult>(
      byId(FOOD_DELIVERY_OPERATIONS_STATUS, integrationId),
      { status }
    );
    return response.data;
  },

  async setWorkingHours(
    integrationId: number,
    intervals: WorkingHoursInterval[]
  ): Promise<OperationResult> {
    const response = await apiClient.post<OperationResult>(
      byId(FOOD_DELIVERY_OPERATIONS_WORKING_HOURS, integrationId),
      { intervals }
    );
    return response.data;
  },

  async setDeliveryDuration(
    integrationId: number,
    minutes: number
  ): Promise<OperationResult> {
    const response = await apiClient.post<OperationResult>(
      byId(FOOD_DELIVERY_OPERATIONS_DELIVERY_DURATION, integrationId),
      { minutes }
    );
    return response.data;
  },

  // ---- Faz 10: monitoring (sağlık özeti + metrik + alert) ----

  async getHealthOverview(branchId: number): Promise<BranchHealthOverview> {
    const response = await apiClient.get<BranchHealthOverview>(
      FOOD_DELIVERY_HEALTH,
      { branchId }
    );
    return response.data;
  },

  async getMetrics(
    branchId: number,
    windowHours?: number
  ): Promise<BranchMetrics> {
    const response = await apiClient.get<BranchMetrics>(FOOD_DELIVERY_METRICS, {
      branchId,
      ...(windowHours ? { windowHours } : {})
    });
    return response.data;
  },

  async getMonitoring(
    branchId: number,
    windowHours?: number
  ): Promise<BranchMonitoring> {
    const response = await apiClient.get<BranchMonitoring>(
      FOOD_DELIVERY_ALERTS,
      { branchId, ...(windowHours ? { windowHours } : {}) }
    );
    return response.data;
  },

  // ---- Faz 11: çok-şube (company-wide) + toplu yönetim ----

  async listByCompany(companyId: number): Promise<FoodIntegration[]> {
    const response = await apiClient.get<FoodIntegration[]>(
      FOOD_DELIVERY_COMPANY_INTEGRATIONS,
      { companyId }
    );
    return response.data;
  },

  async bulkAction(
    action: BulkIntegrationAction,
    integrationIds: number[]
  ): Promise<BulkActionResult> {
    const response = await apiClient.post<BulkActionResult>(
      FOOD_DELIVERY_INTEGRATIONS_BULK,
      { action, integrationIds }
    );
    return response.data;
  },

  async getCompanyHealth(companyId: number): Promise<CompanyHealthOverview> {
    const response = await apiClient.get<CompanyHealthOverview>(
      FOOD_DELIVERY_COMPANY_HEALTH,
      { companyId }
    );
    return response.data;
  },

  async getCompanyMetrics(
    companyId: number,
    windowHours?: number
  ): Promise<CompanyMetrics> {
    const response = await apiClient.get<CompanyMetrics>(
      FOOD_DELIVERY_COMPANY_METRICS,
      { companyId, ...(windowHours ? { windowHours } : {}) }
    );
    return response.data;
  },

  async getCompanyMonitoring(
    companyId: number,
    windowHours?: number
  ): Promise<CompanyMonitoring> {
    const response = await apiClient.get<CompanyMonitoring>(
      FOOD_DELIVERY_COMPANY_ALERTS,
      { companyId, ...(windowHours ? { windowHours } : {}) }
    );
    return response.data;
  },

  // ---- Faz 5: müşteri iadeleri (4 saatlik yanıt süresi) ----

  async listClaims(params: {
    branchId: number;
    status?: ExternalClaimStatus;
    onlyOpen?: boolean;
    limit?: number;
  }): Promise<ExternalClaim[]> {
    const response = await apiClient.get<ExternalClaim[]>(
      FOOD_DELIVERY_CLAIMS,
      {
        branchId: params.branchId,
        ...(params.status ? { status: params.status } : {}),
        ...(params.onlyOpen ? { onlyOpen: 'true' } : {}),
        ...(params.limit ? { limit: params.limit } : {})
      }
    );
    return response.data;
  },

  async getClaimReasons(): Promise<ClaimReasonTables> {
    const response = await apiClient.get<ClaimReasonTables>(
      FOOD_DELIVERY_CLAIM_REASONS
    );
    return response.data;
  },

  async refreshClaims(
    integrationId: number
  ): Promise<{ fetched: number; created: number; updated: number }> {
    const response = await apiClient.post<{
      fetched: number;
      created: number;
      updated: number;
    }>(`${FOOD_DELIVERY_CLAIM_REFRESH}?integrationId=${integrationId}`, {});
    return response.data;
  },

  async acceptClaim(
    claimId: number,
    claimItemIds?: string[]
  ): Promise<{ ok: true }> {
    const response = await apiClient.post<{ ok: true }>(
      byId(FOOD_DELIVERY_CLAIM_ACCEPT, claimId),
      claimItemIds?.length ? { claimItemIds } : {}
    );
    return response.data;
  },

  // ---- Faz 5: kategori / fatura / değerlendirme ----

  async listCategories(integrationId: number): Promise<ExternalMenuCategory[]> {
    const response = await apiClient.get<ExternalMenuCategory[]>(
      byId(FOOD_DELIVERY_CATEGORIES, integrationId)
    );
    return response.data;
  },

  async setCategoryAvailability(
    categoryId: number,
    available: boolean
  ): Promise<{ ok: true }> {
    const response = await apiClient.post<{ ok: true }>(
      byId(FOOD_DELIVERY_CATEGORY_AVAILABILITY, categoryId),
      { available }
    );
    return response.data;
  },

  async sendInvoiceLink(
    orderId: number,
    invoiceLink: string
  ): Promise<{ ok: true }> {
    const response = await apiClient.post<{ ok: true }>(
      byId(FOOD_DELIVERY_ORDER_INVOICE, orderId),
      { invoiceLink }
    );
    return response.data;
  },

  async deleteInvoiceLink(orderId: number): Promise<{ ok: true }> {
    const response = await apiClient.delete<{ ok: true }>(
      byId(FOOD_DELIVERY_ORDER_INVOICE, orderId)
    );
    return response.data;
  },

  async getReviewRating(integrationId: number): Promise<ProviderRatingSummary> {
    const response = await apiClient.get<ProviderRatingSummary>(
      byId(FOOD_DELIVERY_REVIEW_RATING, integrationId)
    );
    return response.data;
  },

  async listReviews(
    integrationId: number,
    params?: { hasComment?: boolean; size?: number }
  ): Promise<ProviderReview[]> {
    const response = await apiClient.get<ProviderReview[]>(
      byId(FOOD_DELIVERY_REVIEWS, integrationId),
      {
        ...(params?.hasComment != null
          ? { hasComment: String(params.hasComment) }
          : {}),
        ...(params?.size ? { size: params.size } : {})
      }
    );
    return response.data;
  },

  async replyToReview(
    integrationId: number,
    reviewId: string,
    text: string
  ): Promise<{ ok: true }> {
    const response = await apiClient.post<{ ok: true }>(
      FOOD_DELIVERY_REVIEW_REPLY.replace(':id', String(integrationId)).replace(
        ':reviewId',
        encodeURIComponent(reviewId)
      ),
      { text }
    );
    return response.data;
  },

  async unresolveClaim(
    claimId: number,
    reasonId: number,
    note?: string,
    claimItemIds?: string[]
  ): Promise<{ ok: true }> {
    const response = await apiClient.post<{ ok: true }>(
      byId(FOOD_DELIVERY_CLAIM_UNRESOLVE, claimId),
      {
        reasonId,
        ...(note ? { note } : {}),
        ...(claimItemIds?.length ? { claimItemIds } : {})
      }
    );
    return response.data;
  }
};
